import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type { FollowEvent } from "../../lib/crm-types";

/**
 * What I want to be told about on this account.
 *
 * Lives on the company rather than in a preferences page, because the choice is
 * about *this* account and nothing else -- a settings screen listing every
 * company somebody might follow would be a list of hundreds of names, and the
 * decision is always made while looking at one of them.
 *
 * Nothing here is on by default and nothing is inherited. Being the account
 * manager does not subscribe you: a manager with two hundred accounts would get
 * a bell nobody could read, and worse, this panel would stop being the whole
 * truth about who hears what.
 */
export function FollowPanel({ companyId }: { companyId: number }) {
  const queryClient = useQueryClient();

  const follow = useQuery({
    queryKey: queryKeys.crmCompanyFollow(companyId),
    queryFn: () => crmApi.companyFollow(companyId),
    staleTime: 5 * 60_000,
  });

  const save = useMutation({
    mutationFn: (events: FollowEvent[]) =>
      crmApi.setCompanyFollow(companyId, events),
    /*
     * Optimistic, because a checkbox that waits for a round trip before moving
     * reads as broken and gets clicked twice. The rollback below is what makes
     * that safe: a failed save puts the box back rather than leaving the page
     * claiming a subscription the server never took.
     */
    onMutate: async (events) => {
      const key = queryKeys.crmCompanyFollow(companyId);
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (old: typeof follow.data) =>
        old ? { ...old, events } : old,
      );

      return { previous };
    },
    onError: (_error, _events, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.crmCompanyFollow(companyId),
          context.previous,
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCompanyFollow(companyId),
      });
    },
  });

  const options = follow.data?.options ?? [];
  const chosen = follow.data?.events ?? [];

  const toggle = (event: FollowEvent, on: boolean): void => {
    const next = on
      ? [...chosen, event]
      : chosen.filter((entry) => entry !== event);

    save.mutate(next);
  };

  return (
    <section className="rounded-lg border border-line">
      <header className="flex items-baseline gap-2 border-b border-line px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-faint">
          Notify me about
        </h2>
        {/* The count, so a collapsed glance says whether this is on at all. */}
        {chosen.length > 0 ? (
          <span className="text-[11px] text-brand-600">
            {chosen.length} of {options.length}
          </span>
        ) : null}
        {save.isError ? (
          <span className="ml-auto text-[11px] text-sla-breached">
            Not saved
          </span>
        ) : null}
      </header>

      <div className="flex flex-col gap-1.5 px-3 py-2.5">
        {follow.isLoading ? (
          <p className="text-[11px] text-faint">Loading…</p>
        ) : (
          options.map((option) => (
            <label
              key={option.event}
              className="flex cursor-pointer items-center gap-2 text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
            >
              <input
                type="checkbox"
                checked={chosen.includes(option.event)}
                onChange={(input) => toggle(option.event, input.target.checked)}
                className="accent-brand-600"
              />
              {option.label}
            </label>
          ))
        )}

        {/*
          Said once, here, rather than left to be discovered. Somebody ticking
          these needs to know the alerts follow them out of the CRM -- and that
          nothing they do themselves will come back at them.
        */}
        {!follow.isLoading ? (
          <p className="pt-1 text-[11px] text-faint">
            {chosen.length === 0
              ? "Nothing from this account reaches you."
              : "Appears in your bell across both products. Your own actions never notify you."}
          </p>
        ) : null}
      </div>
    </section>
  );
}
